/**
 * CruxSection — Chrome UX Report (CrUX) field data for the active
 * site's origin (spec plans/1.1.0/crux-origins). As of phase 2 this
 * is the content of the dedicated **Field data** tab on /performance
 * (a sibling of Real users + Audits — the earlier "no third tab"
 * call was reversed once CrUX grew rich enough to bury the pixel RUM
 * if embedded there).
 *
 * Three variants:
 *
 *   - `full`    — the standalone Field data report: the Chrome UX
 *                 Report banner, a sticky Metric/Device filter bar,
 *                 five CWV tiles, the distribution-over-time chart, the
 *                 LCP-subparts diagnosis card, the per-page + geography
 *                 panels, and the audience block (form factors /
 *                 navigation types / connection). Used by the Field
 *                 data tab.
 *   - `summary` — just the five CWV tile chips (`CruxScoreChips`), the
 *                 same MetricTile row the full report leads with. Used
 *                 by the Overview tab's field-summary card, where it
 *                 sits beside the RUM + audit tile summaries; the card
 *                 header deep-links to the full Field data report. No
 *                 coverage → the five tiles render as empty `—` shells
 *                 (matching the RUM summary), never a note.
 *   - `compact` — a labelled footnote strip (one row of banded p75s)
 *                 expandable into the full report in place. Used on
 *                 the page-detail route for URL-level CrUX context.
 *
 * When the origin/URL has no CrUX coverage a single quiet note
 * renders instead (full always; compact only when `showEmptyNote`;
 * summary shows empty tile shells rather than a note).
 *
 * Everything reads from the weekly series already fetched — the
 * distribution, subparts, and audience sections consume the density,
 * subpart-p75, and fraction columns stored per collection period
 * (phase A). The Device filter is the one control that needs more than
 * the ALL series: the route fetches the PHONE + DESKTOP series and
 * passes them in.
 *
 * The 28-day-window caveat rides in a tooltip throughout: CrUX points
 * are weekly snapshots of a rolling 28-day window, so the numbers are
 * smooth and LAG the daily pixel RUM series by design — that's what
 * Google (and Search) still sees, not a data bug.
 */
import type { CondensedBarsDatum } from '@/components/primitives/CondensedBars';
import { FilterBar } from '@/components/primitives/FilterBar';
import { FilterSelect } from '@/components/primitives/FilterSelect';
import { MetricTile } from '@/components/primitives/MetricTile';
import { Segmented } from '@/components/primitives/Segmented';
import { Tooltip, TooltipPopup, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { rankBand } from '@/lib/crux-geo-metric';
import {
  type MetricBand,
  bandChartFill,
  bandFill,
  bandText,
  clsBand,
  fcpBand,
  formatMs,
  inpBand,
  lcpBand,
  ttfbBand,
} from '@/lib/format';
import { useEntitlement } from '@/lib/queries/billing';
import type { CruxPagesResponse, CruxPeriodRow, CruxResponse } from '@/lib/queries/crux';
import { useSiteCruxHistory } from '@/lib/queries/crux';
import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import {
  CaretDown,
  CaretUp,
  Desktop,
  DeviceMobile,
  GlobeHemisphereWest,
  GoogleChromeLogo,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useState } from 'react';
import { CruxGeography } from './CruxGeography';
import { CruxPagesPanel } from './CruxPagesPanel';
import { CruxPopularityCard } from './CruxPopularityCard';

// ─── Metric registry ────────────────────────────────────────────────

export type CruxMetricKey = 'lcp' | 'inp' | 'cls' | 'ttfb' | 'fcp';

interface CruxMetricSpec {
  key: CruxMetricKey;
  label: string;
  band: (v: number | null | undefined) => MetricBand;
  format: (v: number | null | undefined) => string;
  /** CrUX band thresholds — good/ni boundary and ni/poor boundary. Drive
   *  the reference lines + y-scale of the Value view. */
  good: number;
  poor: number;
}

const formatCls = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : v.toFixed(2);

const CRUX_METRICS: readonly CruxMetricSpec[] = [
  { key: 'lcp', label: 'LCP', band: lcpBand, format: formatMs, good: 2500, poor: 4000 },
  { key: 'inp', label: 'INP', band: inpBand, format: formatMs, good: 200, poor: 500 },
  { key: 'cls', label: 'CLS', band: clsBand, format: formatCls, good: 0.1, poor: 0.25 },
  { key: 'ttfb', label: 'TTFB', band: ttfbBand, format: formatMs, good: 800, poor: 1800 },
  { key: 'fcp', label: 'FCP', band: fcpBand, format: formatMs, good: 1800, poor: 3000 },
];

/** The three Core Web Vitals — the hero's per-vital pass/fail pips,
 *  where showing all five would crowd the verdict line. */
const CORE_METRICS = CRUX_METRICS.filter(
  (m) => m.key === 'lcp' || m.key === 'inp' || m.key === 'cls',
);

// ─── Sticky filter bar ──────────────────────────────────────────────

/** Metric axis for the filter bar: the five vitals (the composite Score
 *  lives in the hero ring, and the distribution chart has no good /
 *  needs-work / poor split to draw for a composite). */
const METRIC_OPTIONS: ReadonlyArray<{ value: CruxMetricKey; label: ReactNode }> = CRUX_METRICS.map(
  (m) => ({ value: m.key, label: m.label }),
);

/** Device (form-factor) axis. CrUX reports ALL / PHONE / DESKTOP; the
 *  route fetches all three, so this just swaps which one the origin
 *  views read. */
type CruxDevice = 'all' | 'phone' | 'desktop';

// Labels stand alone as the dropdown's trigger text — hence "All
// devices" rather than the bare "All" the labelled segmented row used.
const DEVICE_OPTIONS: ReadonlyArray<{ value: CruxDevice; label: string; icon: Icon }> = [
  { value: 'all', label: 'All devices', icon: GlobeHemisphereWest },
  { value: 'phone', label: 'Phone', icon: DeviceMobile },
  { value: 'desktop', label: 'Desktop', icon: Desktop },
];

/** Device picker — the same `FilterSelect` pill Real users and Audits
 *  narrow their audience with. */
function CruxDevicePicker({
  value,
  onChange,
}: {
  value: CruxDevice;
  onChange: (next: CruxDevice) => void;
}): ReactElement {
  const selected = DEVICE_OPTIONS.find((o) => o.value === value) ?? DEVICE_OPTIONS[0]!;
  const SelectedIcon = selected.icon;
  return (
    <FilterSelect
      ariaLabel="Device"
      icon={<SelectedIcon aria-hidden="true" size={12} className="text-[var(--color-mute)]" />}
      value={value}
      options={DEVICE_OPTIONS.map((opt) => {
        const OptionIcon = opt.icon;
        return {
          value: opt.value,
          label: opt.label,
          prefix: <OptionIcon aria-hidden="true" size={13} className="text-[var(--color-mute)]" />,
        };
      })}
      onChange={(next) => onChange(next as CruxDevice)}
      active={value !== 'all'}
    />
  );
}

/** LCP image subparts (phase A), in the order they occur in a load.
 *  `lever` keys the "biggest lever" verdict copy below. Colour is an
 *  opacity step of the brand accent so the stacked bar reads as one
 *  metric decomposed, not four unrelated series. */
const LCP_SUBPARTS: Array<{
  key: keyof CruxPeriodRow;
  label: string;
  lever: 'server' | 'load-delay' | 'load-duration' | 'render-delay';
  opacity: number;
}> = [
  { key: 'lcp_img_ttfb_p75', label: 'Server (TTFB)', lever: 'server', opacity: 0.9 },
  { key: 'lcp_img_load_delay_p75', label: 'Load delay', lever: 'load-delay', opacity: 0.68 },
  { key: 'lcp_img_load_duration_p75', label: 'Load time', lever: 'load-duration', opacity: 0.46 },
  { key: 'lcp_img_render_delay_p75', label: 'Render delay', lever: 'render-delay', opacity: 0.26 },
];

const LEVER_VERDICT: Record<(typeof LCP_SUBPARTS)[number]['lever'], string> = {
  server:
    'Server response (TTFB) is the biggest slice of LCP — a faster backend or CDN edge caching is the highest-leverage fix.',
  'load-delay':
    'Image load delay dominates LCP — preload the LCP image and raise its fetchpriority so the browser requests it sooner.',
  'load-duration':
    'Image download time dominates LCP — compress and correctly size the LCP image and serve it from a CDN.',
  'render-delay':
    'Render delay dominates LCP — cut render-blocking CSS/JS so the already-loaded image can paint.',
};

/** Form-factor fraction columns → labels, for the audience block. */
const FORM_FACTOR_ROWS: Array<{ key: keyof CruxPeriodRow; label: string }> = [
  { key: 'ff_phone', label: 'Phone' },
  { key: 'ff_desktop', label: 'Desktop' },
  { key: 'ff_tablet', label: 'Tablet' },
];

/** Navigation-type fraction columns → labels. */
const NAV_TYPE_ROWS: Array<{ key: keyof CruxPeriodRow; label: string }> = [
  { key: 'nav_navigate', label: 'Navigate' },
  { key: 'nav_navigate_cache', label: 'Cache' },
  { key: 'nav_back_forward', label: 'Back/forward' },
  { key: 'nav_back_forward_cache', label: 'bfcache' },
  { key: 'nav_reload', label: 'Reload' },
  { key: 'nav_restore', label: 'Restore' },
  { key: 'nav_prerender', label: 'Prerender' },
];

const WINDOW_CAVEAT =
  'Chrome UX Report values are 28-day rolling windows snapshotted weekly, so they lag ' +
  'sudden changes — this is what Google (and Google Search) currently sees for the origin.';

function p75Of(row: CruxPeriodRow | null | undefined, key: CruxMetricKey): number | null {
  return row ? row[`${key}_p75`] : null;
}

type DensityBin = 'good' | 'ni' | 'poor';

function densityOf(row: CruxPeriodRow, key: CruxMetricKey, bin: DensityBin): number | null {
  return row[`${key}_${bin}`];
}

const pct = (v: number | null): string => (v == null ? '—' : `${Math.round(v * 100)}%`);

/** Read a raw column off the latest period (Phase A fields are
 *  optional on the row type — coalesce undefined → null). */
function fieldOf(row: CruxPeriodRow | null | undefined, key: keyof CruxPeriodRow): number | null {
  const v = row?.[key];
  return typeof v === 'number' ? v : null;
}

/** Band a 0–100 field score for colour. Mirrors the API weighting's
 *  intent: 90+ reads as healthy, 50–89 as work-to-do, below as poor. */
function scoreBand(score: number | null | undefined): MetricBand {
  if (score == null) return 'unknown';
  if (score >= 90) return 'good';
  if (score >= 50) return 'needs-improvement';
  return 'poor';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `YYYY-MM-DD` → human date. period_end is a fixed-format string, so
 *  parse it directly rather than through Date() (avoids TZ drift). */
function fmtDay(iso: string | null | undefined, withYear = false): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const mon = MONTHS[Number(m) - 1] ?? m;
  return withYear ? `${Number(d)} ${mon} ${y}` : `${Number(d)} ${mon}`;
}

/** The CrUX "good" threshold: a metric passes CWV when ≥75% of loads
 *  are good (equivalently, its p75 is in the good range). */
const PASS_THRESHOLD = 0.75;

// ─── Component ──────────────────────────────────────────────────────

export interface CruxSectionProps {
  data: CruxResponse | undefined;
  variant: 'full' | 'compact' | 'summary';
  /** Per-device series feeding the Device filter (full variant).
   *  Optional — the compact strip and any caller that doesn't fetch
   *  them just falls back to the ALL series. */
  phone?: CruxResponse | undefined;
  desktop?: CruxResponse | undefined;
  /** Per-page CrUX for the worst-pages table (full variant). */
  pages?: CruxPagesResponse | undefined;
  /** compact only: also render the no-coverage note when the origin /
   *  page isn't in CrUX. The routes set this where an unexplained
   *  absence would read as broken (pixel-less empty state; page
   *  detail, where sparse URL coverage is the NORMAL case). */
  showEmptyNote?: boolean;
  /** Adjusts the no-coverage copy: origin-level absence means low
   *  site traffic; URL-level absence is routine (Google only
   *  publishes individually-popular pages). Defaults to origin. */
  scope?: 'origin' | 'url';
  /** Site uuid — enables the (paid) BigQuery Geography section + the
   *  popularity-rank hero line in the full report. Omit on the URL /
   *  page scope, which has no origin-level geography. */
  siteUuid?: string;
  /** `full` only: the report's selected metric, lifted so the route can
   *  back it with `?metric=` (shareable, and the Overview's field card
   *  can deep-link into it). Omit and the report keeps it internally. */
  metric?: CruxMetricKey;
  onMetricChange?: (m: CruxMetricKey) => void;
  /** `summary` only: makes each CWV chip a click-through for that
   *  metric — the Overview card wires it to the full Field data report.
   *  Omit and the chips stay inert. */
  onMetricSelect?: (m: CruxMetricKey) => void;
  className?: string;
}

export function CruxSection({
  data,
  variant,
  phone,
  desktop,
  pages,
  showEmptyNote = false,
  scope = 'origin',
  siteUuid,
  metric,
  onMetricChange,
  onMetricSelect,
  className,
}: CruxSectionProps): ReactElement | null {
  // Summary → always the five CWV tile chips (empty `—` shells when
  // there's no data yet or no coverage), so the Overview's field-summary
  // card stays visually identical to its RUM + audit tile summaries. The
  // card header owns the deep-link into the full report; each chip
  // deep-links into that report at its own metric.
  if (variant === 'summary') {
    return (
      <CruxScoreChips
        data={data}
        {...(onMetricSelect ? { onMetricSelect } : {})}
        className={className}
      />
    );
  }

  if (!data) return null;

  if (data.coverage !== 'ok' || data.series.length === 0) {
    const message =
      data.coverage !== 'no_data'
        ? 'Chrome UX Report data hasn’t been fetched yet — check back soon.'
        : scope === 'url'
          ? 'No page-level Chrome UX Report data for this URL — Google only publishes field data for individually popular pages, so this is normal.'
          : "This origin isn't in the Chrome UX Report yet — Google publishes field data once a site has enough Chrome traffic.";
    // full → the route chose CrUX as the main content but there is
    // none; render the report's own chrome empty with the explanation
    // over it, rather than a lone sentence on a blank tab. compact →
    // stay a one-line footnote unless silent (the strip has no frame
    // to hold open).
    if (variant === 'full') return <CruxEmptyReport message={message} className={className} />;
    if (showEmptyNote) {
      return <p className={cn('text-xs text-[var(--color-ink-soft)]', className)}>{message}</p>;
    }
    return null;
  }

  if (variant === 'compact') return <CruxStrip data={data} className={className} />;
  return (
    <CruxReport
      data={data}
      phone={phone}
      desktop={desktop}
      pages={pages}
      siteUuid={siteUuid}
      metric={metric}
      onMetricChange={onMetricChange}
      className={className}
    />
  );
}

/** A CrUX response with nothing in it — feeds the empty report frame so
 *  the hero, tiles and chart render their own no-data shells (`—` score
 *  ring, unknown-band pips, placeholder bars) instead of being absent. */
const EMPTY_RESPONSE: CruxResponse = {
  coverage: 'no_data',
  last_fetched_at: null,
  form_factor: 'ALL',
  field_score: null,
  cwv: null,
  latest: null,
  series: [],
};

/**
 * The Field-data report with no data in it: the Chrome UX Report banner,
 * the filter bar, the five `—` metric tiles and the distribution chart's
 * axis furniture — the same shapes that fill in once Google publishes
 * the origin, held open and dimmed.
 *
 * With `message` (no coverage) the explanation sits over the frame, the
 * way FadedCover covers a gated card. Without one (still fetching) the
 * frame is the loading state, matching the Real-users tab's "bordered
 * frames, no shimmer" convention.
 */
export function CruxEmptyReport({
  message,
  className,
}: {
  message?: string;
  className?: string;
}): ReactElement {
  return (
    <div className={cn('relative', className)}>
      <div
        aria-hidden="true"
        className={cn('space-y-6 select-none', message ? 'opacity-40' : 'opacity-60')}
      >
        <CruxHero data={EMPTY_RESPONSE} />
        {/* Inert: the axes exist so the shape reads, but there's nothing
            for the controls to filter. `static` overrides the bar's
            sticky positioning — pinning an unusable control is noise. */}
        <div className="pointer-events-none [&>div]:static">
          <CruxFilterBar metric="lcp" onMetric={noop} device="all" onDevice={noop} cwv={null} />
        </div>
        <CruxScoreChips />
        <div className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
          <h3 className="text-sm font-medium text-[var(--color-ink)]">
            LCP{' '}
            <span className="font-normal text-[var(--color-mute)]">
              · good / needs-work / poor by week
            </span>
          </h3>
          {/* Plot area only — no 75% pass line. A pass threshold with
              nothing measured against it reads as a verdict. */}
          <div className="h-36" />
        </div>
      </div>
      {message && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center p-4">
          <div className="flex max-w-sm flex-col items-center gap-2 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] px-6 py-5 text-center shadow-md">
            <GoogleChromeLogo
              aria-hidden="true"
              size={28}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <p className="text-xs text-[var(--color-ink-soft)]">{message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const noop = (): void => {};

/** Full variant: the standalone Field-data report. */
function CruxReport({
  data,
  phone,
  desktop,
  pages,
  siteUuid,
  metric: metricProp,
  onMetricChange,
  className,
}: {
  data: CruxResponse;
  phone?: CruxResponse | undefined;
  desktop?: CruxResponse | undefined;
  pages?: CruxPagesResponse | undefined;
  siteUuid?: string;
  /** Lifted metric selection — see `CruxSectionProps`. Both absent ⇒
   *  the report falls back to its own local state. */
  metric?: CruxMetricKey | undefined;
  onMetricChange?: ((m: CruxMetricKey) => void) | undefined;
  className?: string;
}): ReactElement {
  const { latest } = data;
  // BigQuery popularity rank (paid) for the hero line. Separate source
  // from the weekly series above, with its own coverage — the line just
  // hides when there's no rank. History query stays disabled unless the
  // workspace is entitled + we have a site uuid.
  const { allowed: geoAllowed } = useEntitlement('crux_geo');
  const history = useSiteCruxHistory(siteUuid ?? null, {
    enabled: geoAllowed && !!siteUuid,
  });
  const rank = geoAllowed ? (history.data?.rank ?? null) : null;
  // The sticky filter bar's two axes. Metric (five vitals; Score lives
  // in the hero ring) re-keys the distribution chart, Pages panel and
  // map. Device swaps the active form-factor series for the filtered
  // views below the bar (tiles, distribution, subparts). The hero stays
  // the stable all-devices banner above the bar; the map is phone-only
  // and Pages is all-devices, so those keep their own basis.
  // Controlled when the route supplies `metric` (URL-backed), local
  // otherwise — Device stays local either way, it's a display knob the
  // Overview has no reason to deep-link into.
  const [localMetric, setLocalMetric] = useState<CruxMetricKey>('lcp');
  const metric = metricProp ?? localMetric;
  const setMetric = onMetricChange ?? setLocalMetric;
  const [device, setDevice] = useState<CruxDevice>('all');
  const active =
    device === 'phone' ? (phone ?? data) : device === 'desktop' ? (desktop ?? data) : data;

  return (
    <section className={cn('space-y-6 animate-in fade-in duration-300', className)}>
      {/* The Chrome UX Report banner: the stable, all-devices header. */}
      <CruxHero data={data} rank={rank} />

      {/* Filter bar directly under the banner, so the controls sit next
          to the data they actually change (tiles / chart / map below);
          mirrors the Audits tab's sticky filter strip. */}
      <CruxFilterBar
        metric={metric}
        onMetric={setMetric}
        device={device}
        onDevice={setDevice}
        cwv={data.cwv ?? null}
      />

      {/* Headline: the five CWV at p75 with 28-week sparkbars. */}
      <CruxScoreChips data={active} />

      <CruxDistribution series={active.series} metric={metric} />
      <CruxSubparts latest={active.latest} />
      <div className="grid items-start gap-4 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
        <CruxPagesPanel siteUuid={siteUuid ?? null} pages={pages} metric={metric} />
        <CruxPopularityCard siteUuid={siteUuid ?? null} />
      </div>
      <CruxGeography siteUuid={siteUuid ?? null} metric={metric} />
      <CruxAudience latest={latest} />
    </section>
  );
}

/** The report's sticky, full-width filter bar — mirrors the Audits
 *  tab's strip (AuditMetricCharts): full-bleed via negative margins,
 *  pinned flush to the Island padding-box top. Metric re-keys the
 *  distribution chart, Pages panel and map; Device swaps the active
 *  form-factor series for the origin-level views. */
function CruxFilterBar({
  metric,
  onMetric,
  device,
  onDevice,
  cwv,
}: {
  metric: CruxMetricKey;
  onMetric: (m: CruxMetricKey) => void;
  device: CruxDevice;
  onDevice: (d: CruxDevice) => void;
  cwv: 'pass' | 'fail' | null;
}): ReactElement {
  return (
    // Not flush: the Chrome UX Report banner leads this page, so the
    // Island's top padding is doing real work above it.
    <FilterBar flush={false}>
      <Segmented label="Metric" value={metric} options={METRIC_OPTIONS} onChange={onMetric} />
      <CruxDevicePicker value={device} onChange={onDevice} />
      {/* The pass/fail verdict lives here (not the hero) so it stays pinned
          in view as you scroll the report. Right-aligned via ml-auto. */}
      {cwv && (
        <span className="ml-auto inline-flex items-center gap-2">
          <span className="hidden text-[11px] font-medium uppercase tracking-wide text-[var(--color-mute)] sm:inline">
            Core Web Vitals
          </span>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
              cwv === 'pass'
                ? 'bg-[var(--color-success-soft)] text-[var(--color-success)]'
                : 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
            )}
          >
            {cwv === 'pass' ? 'Passing' : 'Failing'}
          </span>
        </span>
      )}
    </FilterBar>
  );
}

/** The five CWV at p75 as MetricTile chips with 28-week sparkbars — the
 *  row the full report leads with, and the whole of the `summary`
 *  variant. `data` may be undefined / uncovered: each tile then renders
 *  an empty `—` shell, so the Overview's field summary matches the RUM
 *  tile row's empty state rather than vanishing or nagging. */
function CruxScoreChips({
  data,
  onMetricSelect,
  className,
}: {
  data?: CruxResponse;
  /** Makes each chip a click-through for its metric (Overview card
   *  only). Ignored on the `—` shells: with no coverage there's
   *  nothing in the full report to drill into. */
  onMetricSelect?: (m: CruxMetricKey) => void;
  className?: string;
}): ReactElement {
  const latest = data?.latest ?? null;
  const series = data?.series ?? [];
  return (
    <div
      className={cn(
        'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]',
        className,
      )}
    >
      {CRUX_METRICS.map((m) => {
        const value = p75Of(latest, m.key);
        const bars: CondensedBarsDatum<{ periodEnd: string }>[] = series.map((row) => ({
          x: Date.parse(row.period_end),
          y: p75Of(row, m.key),
          meta: { periodEnd: row.period_end },
        }));
        return (
          <MetricTile
            key={m.key}
            label={m.label}
            value={m.format(value)}
            band={value == null ? 'unknown' : m.band(value)}
            bars={bars}
            variant="chip"
            barBand={(y) => m.band(y)}
            barTitle={(d) => `Week ending ${fmtDay(d.meta?.periodEnd, true)} · ${m.format(d.y)}`}
            {...(onMetricSelect && value != null
              ? {
                  onClick: () => onMetricSelect(m.key),
                  actionLabel: `Explore ${m.label} in Field data`,
                }
              : {})}
          />
        );
      })}
    </div>
  );
}

// ─── Hero (score + CWV assessment) ──────────────────────────────────

/** Report header: source, the per-vital Core Web Vitals breakdown, and
 *  the composite Field score ring. The pass/fail verdict lives in the
 *  sticky filter bar (CruxFilterBar) so it stays pinned while scrolling. */
function CruxHero({ data, rank }: { data: CruxResponse; rank?: number | null }): ReactElement {
  const score = data.field_score ?? null;
  const band = scoreBand(score);
  const latest = data.latest ?? null;
  const latestEnd = latest?.period_end;
  const popularity = rankBand(rank);
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-start gap-2.5">
        <GoogleChromeLogo
          size={22}
          weight="light"
          className="mt-0.5 shrink-0 text-[var(--color-ink-soft)]"
        />
        <div>
          <h2 className="text-sm font-semibold text-[var(--color-ink)]">Chrome UX Report</h2>
          {popularity && (
            <p className="mt-1.5 text-[11px] text-[var(--color-mute)]">
              Popularity:{' '}
              <span className="font-medium text-[var(--color-ink-soft)]">{popularity}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4">
        {/* The three Core Web Vitals as status pips — which-vital-is-weak at
            a glance. The pass/fail verdict is labelled in the sticky bar;
            the composite score is the ring beside. */}
        <div className="flex items-center gap-3">
          {CORE_METRICS.map((m) => {
            const value = p75Of(latest, m.key);
            const b: MetricBand = value == null ? 'unknown' : m.band(value);
            return (
              <span
                key={m.key}
                className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-ink-soft)]"
                title={`${m.label} p75 · ${m.format(value)}`}
              >
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: bandChartFill(b) }}
                  aria-hidden="true"
                />
                {m.label}
              </span>
            );
          })}
        </div>
        <ScoreRing
          score={score}
          band={band}
          freshness={latestEnd ? `wk ending ${fmtDay(latestEnd)}` : undefined}
        />
      </div>
    </div>
  );
}

/** Small donut gauge for the composite field score — demoted from the
 *  old headline number so the CWV verdict + per-vital pips lead. Fills
 *  `score`% in the score's band colour; the arc starts at 12 o'clock
 *  (SVG rotated −90°) while the centred number stays upright as a
 *  sibling, not a rotated child. */
function ScoreRing({
  score,
  band,
  freshness,
}: {
  score: number | null;
  band: MetricBand;
  freshness?: string | undefined;
}): ReactElement {
  const r = 20;
  const circ = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  const dash = (pct / 100) * circ;
  const label = `Field score ${score ?? 'unavailable'}${freshness ? ` · ${freshness}` : ''}`;
  return (
    <TooltipProvider delay={0}>
      <Tooltip>
        <TooltipTrigger
          render={
            <div className="flex shrink-0 cursor-help flex-col items-center gap-1">
              <div className="relative size-[52px]">
                <svg
                  viewBox="0 0 52 52"
                  className="size-full -rotate-90"
                  role="img"
                  aria-label={label}
                >
                  <circle
                    cx={26}
                    cy={26}
                    r={r}
                    fill="none"
                    stroke="var(--color-line)"
                    strokeWidth={4}
                  />
                  {score != null && (
                    <circle
                      cx={26}
                      cy={26}
                      r={r}
                      fill="none"
                      stroke={bandFill(band)}
                      strokeWidth={4}
                      strokeLinecap="round"
                      strokeDasharray={`${dash} ${circ}`}
                    />
                  )}
                </svg>
                <span
                  className={cn(
                    'absolute inset-0 flex items-center justify-center text-sm font-semibold tabular-nums',
                    bandText(band),
                  )}
                >
                  {score ?? '—'}
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-wide text-[var(--color-mute)]">
                Field score
              </span>
            </div>
          }
        />
        <TooltipPopup side="top" sideOffset={6} className="max-w-xs normal-case">
          A 0–100 composite of your Core Web Vitals — the weighted share of real-user loads hitting
          Google’s “good” threshold across LCP, INP, CLS, TTFB and FCP. Higher is better; the ring
          fills to the score.
        </TooltipPopup>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Distribution-over-time chart (the Treo-style CrUX read) with two
 * views, toggled top-right:
 *   - Distribution — one stacked column per weekly period, split into
 *     the good / needs-improvement / poor share of loads (good anchors
 *     the bottom, under a 75% pass line).
 *   - Value — one bar per period at the metric's p75, coloured by its
 *     band, with the good + poor CrUX thresholds as reference lines.
 */
function CruxDistribution({
  series,
  metric,
}: {
  series: CruxPeriodRow[];
  metric: CruxMetricKey;
}): ReactElement {
  const [view, setView] = useState<DistView>('distribution');
  const spec = CRUX_METRICS.find((m) => m.key === metric) ?? CRUX_METRICS[0]!;
  const latest = series[series.length - 1] ?? null;
  const latestP75 = p75Of(latest, metric);
  const latestGood = latest ? densityOf(latest, metric, 'good') : null;

  return (
    <div className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-[var(--color-ink)]">
          {spec.label}{' '}
          <span className="font-normal text-[var(--color-mute)]">
            {view === 'value' ? '· p75 by week' : '· good / needs-work / poor by week'}
          </span>
        </h3>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-3 text-xs text-[var(--color-ink-soft)] sm:flex">
            {(
              [
                ['good', 'Good'],
                ['needs-improvement', 'Needs work'],
                ['poor', 'Poor'],
              ] as const
            ).map(([band, label]) => (
              <span key={band} className="inline-flex items-center gap-1.5">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: bandChartFill(band) }}
                  aria-hidden="true"
                />
                {label}
              </span>
            ))}
          </div>
          <Segmented label="View" value={view} options={DIST_VIEW_OPTIONS} onChange={setView} />
        </div>
      </div>

      {view === 'value' ? (
        <ValueBars series={series} metric={metric} spec={spec} />
      ) : (
        <DistributionBars series={series} metric={metric} spec={spec} />
      )}

      <div className="flex items-center justify-between text-[11px] text-[var(--color-mute)]">
        <span>{fmtDay(series[0]?.period_end, true)}</span>
        <span>
          latest:{' '}
          <span className={bandText(spec.band(latestP75))}>
            {view === 'value' ? spec.format(latestP75) : `${pct(latestGood)} good`}
          </span>
          {view === 'value'
            ? ''
            : latestGood != null
              ? latestGood >= PASS_THRESHOLD
                ? ' — passing'
                : ' — below 75%'
              : ''}
        </span>
        <span>{fmtDay(latest?.period_end, true)}</span>
      </div>
    </div>
  );
}

type DistView = 'distribution' | 'value';

const DIST_VIEW_OPTIONS: ReadonlyArray<{ value: DistView; label: ReactNode }> = [
  { value: 'value', label: 'Value' },
  { value: 'distribution', label: 'Distribution' },
];

/** Distribution view: stacked good / needs-improvement / poor columns
 *  under the 75% pass line. */
function DistributionBars({
  series,
  metric,
  spec,
}: {
  series: CruxPeriodRow[];
  metric: CruxMetricKey;
  spec: CruxMetricSpec;
}): ReactElement {
  return (
    <div className="relative h-36">
      {/* Bars sit under a 75%-good pass line: a metric passes CWV when
          the green (good) share — anchored at the bottom — rises above
          it. That makes "are we passing?" a direct read. */}
      <div
        className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-dashed border-[var(--color-ink)]/75"
        style={{ bottom: `${PASS_THRESHOLD * 100}%` }}
      >
        <span className="absolute right-0 top-0 -translate-y-1/2 rounded bg-[var(--color-ink)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-paper)] shadow-sm">
          75% · pass
        </span>
      </div>
      <div
        className="flex h-full items-end gap-[3px]"
        role="img"
        aria-label={`${spec.label} distribution by week`}
      >
        {series.map((row) => {
          const good = densityOf(row, metric, 'good');
          const ni = densityOf(row, metric, 'ni');
          const poor = densityOf(row, metric, 'poor');
          const total = (good ?? 0) + (ni ?? 0) + (poor ?? 0);
          const title =
            total === 0
              ? `Week ending ${fmtDay(row.period_end, true)} · no ${spec.label} data`
              : `Week ending ${fmtDay(row.period_end, true)} · p75 ${spec.format(p75Of(row, metric))} · ${pct(good)} good · ${pct(ni)} needs work · ${pct(poor)} poor`;
          return (
            <div
              key={row.period_end}
              className="flex h-full flex-1 flex-col justify-end"
              title={title}
            >
              {total === 0 ? (
                <div className="h-1 rounded-sm bg-[var(--color-line-soft)]" />
              ) : (
                <div className="flex h-full flex-col overflow-hidden rounded-sm">
                  {(
                    [
                      ['poor', poor],
                      ['needs-improvement', ni],
                      ['good', good],
                    ] as const
                  ).map(([band, share]) => (
                    <div
                      key={band}
                      style={{
                        height: `${((share ?? 0) / total) * 100}%`,
                        backgroundColor: bandChartFill(band),
                        opacity: band === 'good' ? 0.85 : 0.9,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Value view: one bar per period at the p75, coloured by band, scaled
 *  so the poor threshold + every bar fit, with the good + poor CrUX
 *  boundaries drawn as reference lines. */
function ValueBars({
  series,
  metric,
  spec,
}: {
  series: CruxPeriodRow[];
  metric: CruxMetricKey;
  spec: CruxMetricSpec;
}): ReactElement {
  const yMax = Math.max(spec.poor, ...series.map((r) => p75Of(r, metric) ?? 0)) * 1.08;
  return (
    <div className="relative h-36">
      <ThresholdLine
        pct={(spec.good / yMax) * 100}
        label={spec.format(spec.good)}
        band="needs-improvement"
      />
      <ThresholdLine pct={(spec.poor / yMax) * 100} label={spec.format(spec.poor)} band="poor" />
      <div
        className="flex h-full items-end gap-[3px]"
        role="img"
        aria-label={`${spec.label} p75 by week`}
      >
        {series.map((row) => {
          const v = p75Of(row, metric);
          const band: MetricBand = v == null ? 'unknown' : spec.band(v);
          const hPct = v == null ? 0 : Math.min(100, (v / yMax) * 100);
          const title =
            v == null
              ? `Week ending ${fmtDay(row.period_end, true)} · no ${spec.label} data`
              : `Week ending ${fmtDay(row.period_end, true)} · p75 ${spec.format(v)}`;
          return (
            <div
              key={row.period_end}
              className="flex h-full flex-1 flex-col justify-end"
              title={title}
            >
              {v == null ? (
                <div className="h-1 rounded-sm bg-[var(--color-line-soft)]" />
              ) : (
                <div
                  className="rounded-sm"
                  style={{
                    height: `${Math.max(2, hPct)}%`,
                    backgroundColor: bandChartFill(band),
                    opacity: 0.9,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A dashed horizontal reference line at a band boundary, labelled with
 *  the threshold value — the Value view's y-axis marks. */
function ThresholdLine({
  pct,
  label,
  band,
}: {
  pct: number;
  label: string;
  band: MetricBand;
}): ReactElement {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-10 border-t border-dashed"
      style={{ bottom: `${pct}%`, borderColor: bandFill(band), opacity: 0.75 }}
    >
      <span
        className="absolute right-0 top-0 -translate-y-1/2 rounded bg-[var(--color-paper)] pl-1 text-[10px] font-medium tabular-nums"
        style={{ color: bandFill(band) }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── LCP subparts (the diagnosis card) ──────────────────────────────

/**
 * Decomposes LCP into its four sub-phases (TTFB → image load delay →
 * image load time → render delay) as a stacked bar, and calls out the
 * biggest lever. Google publishes these only for the image-LCP share
 * of loads, so the card hides itself when no subpart is present (text
 * LCP, or pre-backfill data).
 */
function CruxSubparts({ latest }: { latest: CruxPeriodRow | null }): ReactElement | null {
  const parts = LCP_SUBPARTS.map((p) => ({ ...p, ms: fieldOf(latest, p.key) })).filter(
    (p): p is typeof p & { ms: number } => p.ms != null && p.ms > 0,
  );
  if (parts.length === 0) return null;

  const total = parts.reduce((sum, p) => sum + p.ms, 0);
  const biggest = parts.reduce((a, b) => (b.ms > a.ms ? b : a));
  const imgShare = fieldOf(latest, 'lcp_rt_image');
  const textShare = fieldOf(latest, 'lcp_rt_text');

  return (
    <div className="space-y-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-[var(--color-ink)]">What makes up LCP</h3>
        {imgShare != null && (
          <span className="text-[11px] text-[var(--color-mute)]">
            image {pct(imgShare)}
            {textShare != null ? ` · text ${pct(textShare)}` : ''}
          </span>
        )}
      </div>

      <div className="flex h-3 overflow-hidden rounded-full" role="img" aria-label="LCP breakdown">
        {parts.map((p) => (
          <div
            key={p.label}
            style={{
              width: `${(p.ms / total) * 100}%`,
              backgroundColor: 'var(--color-accent-brand)',
              opacity: p.opacity,
            }}
            title={`${p.label}: ${formatMs(p.ms)} (${Math.round((p.ms / total) * 100)}%)`}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {parts.map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1.5 text-xs">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: 'var(--color-accent-brand)', opacity: p.opacity }}
              aria-hidden="true"
            />
            <span className="text-[var(--color-ink-soft)]">{p.label}</span>
            <span className="font-medium tabular-nums text-[var(--color-ink)]">
              {formatMs(p.ms)}
            </span>
          </span>
        ))}
      </div>

      <p className="border-t border-[var(--color-line-soft)] pt-2 text-xs text-[var(--color-ink-soft)]">
        {LEVER_VERDICT[biggest.lever]}
      </p>
    </div>
  );
}

// ─── Audience ───────────────────────────────────────────────────────

/** Horizontal share rows — one bar per label, non-zero only, desc. */
function ShareRows({
  rows,
}: {
  rows: Array<{ label: string; value: number | null }>;
}): ReactElement | null {
  const present = rows
    .filter((r): r is { label: string; value: number } => r.value != null && r.value > 0.0005)
    .sort((a, b) => b.value - a.value);
  if (present.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {present.map((r) => (
        <div key={r.label} className="flex items-center gap-2 text-xs">
          <span className="w-24 shrink-0 truncate text-[var(--color-ink-soft)]">{r.label}</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--color-line-soft)]">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.round(r.value * 100)}%`,
                backgroundColor: 'var(--color-accent-brand)',
                opacity: 0.7,
              }}
            />
          </div>
          <span className="w-9 shrink-0 text-right font-medium tabular-nums text-[var(--color-ink)]">
            {pct(r.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Audience block: who Google saw and how they arrived — form-factor
 * mix, navigation types, and connection quality (RTT). All from the
 * fraction/RTT columns on the latest period (phase A). Hides itself
 * entirely on pre-backfill data where none are present.
 */
function CruxAudience({ latest }: { latest: CruxPeriodRow | null }): ReactElement | null {
  const formFactors = FORM_FACTOR_ROWS.map((r) => ({
    label: r.label,
    value: fieldOf(latest, r.key),
  }));
  const navTypes = NAV_TYPE_ROWS.map((r) => ({ label: r.label, value: fieldOf(latest, r.key) }));
  const rttP75 = fieldOf(latest, 'rtt_p75');
  const rtt = [
    { label: 'Low (<75ms)', value: fieldOf(latest, 'rtt_low'), fill: 'var(--color-success)' },
    { label: 'Medium', value: fieldOf(latest, 'rtt_medium'), fill: 'var(--color-warning)' },
    { label: 'High (>275ms)', value: fieldOf(latest, 'rtt_high'), fill: 'var(--color-danger)' },
  ];

  const hasFf = formFactors.some((r) => r.value != null);
  const hasNav = navTypes.some((r) => r.value != null);
  const hasRtt = rttP75 != null || rtt.some((r) => r.value != null);
  if (!hasFf && !hasNav && !hasRtt) return null;

  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <h3 className="mb-3 text-sm font-medium text-[var(--color-ink)]">Audience</h3>
      <div className="grid gap-x-6 gap-y-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        {hasFf && (
          <div className="space-y-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-mute)]">
              Device
            </span>
            <ShareRows rows={formFactors} />
          </div>
        )}
        {hasNav && (
          <div className="space-y-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-mute)]">
              How they arrived
            </span>
            <ShareRows rows={navTypes} />
          </div>
        )}
        {hasRtt && (
          <div className="space-y-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-mute)]">
              Connection (RTT{rttP75 != null ? ` · p75 ${formatMs(rttP75)}` : ''})
            </span>
            <div className="flex h-2 overflow-hidden rounded-full">
              {rtt
                .filter((r) => r.value != null && r.value > 0)
                .map((r) => (
                  <div
                    key={r.label}
                    style={{ width: `${(r.value ?? 0) * 100}%`, backgroundColor: r.fill }}
                    title={`${r.label}: ${pct(r.value ?? null)}`}
                  />
                ))}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {rtt
                .filter((r) => r.value != null && r.value > 0)
                .map((r) => (
                  <span key={r.label} className="inline-flex items-center gap-1 text-[11px]">
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: r.fill }}
                      aria-hidden="true"
                    />
                    <span className="text-[var(--color-ink-soft)]">{r.label}</span>
                    <span className="tabular-nums text-[var(--color-ink)]">{pct(r.value)}</span>
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact variant: one footnote row of banded p75s, expandable into
 *  the full report (tiles + distribution chart) in place. */
function CruxStrip({ data, className }: { data: CruxResponse; className?: string }): ReactElement {
  const { latest } = data;
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={cn('space-y-4', className)}>
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-ink-soft)]"
        title={WINDOW_CAVEAT}
      >
        <span className="inline-flex items-center gap-1.5 font-medium">
          <GoogleChromeLogo size={14} weight="light" />
          Chrome UX Report
        </span>
        {CRUX_METRICS.map((m) => {
          const value = p75Of(latest, m.key);
          return (
            <span key={m.key} className="inline-flex items-baseline gap-1">
              <span>{m.label}</span>
              <span
                className={cn(
                  'font-medium tabular-nums',
                  value == null ? 'text-[var(--color-mute)]' : bandText(m.band(value)),
                )}
              >
                {m.format(value)}
              </span>
            </span>
          );
        })}
        {latest && (
          <span className="text-[var(--color-mute)]">
            wk ending {fmtDay(latest.period_end, true)}
          </span>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-[var(--color-ink-soft)] transition-colors hover:bg-[var(--color-paper-alt)] hover:text-[var(--color-ink)]"
        >
          {expanded ? 'Hide report' : 'Full report'}
          {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </button>
      </div>
      {expanded && <CruxReport data={data} />}
    </div>
  );
}
